import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { getErrorMessage } from '@/utils/errorMessage';
import { PatientService } from '../patient.service';

const resolveCreatePatientInput = (
	input: Patients.Create | Patients.CreatePatientInput,
): { body: Patients.Create; professionalId?: string } => {
	if ('body' in input) {
		return {
			body: input.body,
			professionalId: input.professionalId,
		};
	}

	return { body: input };
};

export const useCreatePatient = (
	onSuccess?: (patient: Patients.Patient) => void,
) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (input: Patients.Create | Patients.CreatePatientInput) => {
			const { body, professionalId } = resolveCreatePatientInput(input);
			return PatientService.create(body, professionalId);
		},
		onSuccess: (patient) => {
			queryClient.invalidateQueries({ queryKey: ['patients'] });
			toast.success('Paciente criado com sucesso!');
			if (onSuccess) onSuccess(patient);
		},
		onError: (error: unknown) => {
			toast.error(getErrorMessage(error, 'Erro na criação do paciente'));
		},
	});
};

export const useUpdatePatient = (
	onSuccess?: (patient: Patients.Patient) => void,
) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ id, body }: { id: string; body: Patients.Update }) =>
			PatientService.patch(id, body),
		onSuccess: (patient) => {
			queryClient.invalidateQueries({ queryKey: ['patients'] });
			queryClient.invalidateQueries({
				queryKey: ['patient', patient.id],
			});
			toast.success('Paciente atualizado com sucesso!');
			if (onSuccess) onSuccess(patient);
		},
		onError: (error: unknown) => {
			toast.error(
				getErrorMessage(error, 'Erro na atualização do paciente'),
			);
		},
	});
};

export const useRemovePatient = (onSuccess?: () => void) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (id: string) => PatientService.remove(id),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['patients'] });
			toast.success('Paciente removido com sucesso!');
			if (onSuccess) onSuccess();
		},
		onError: (error: unknown) => {
			toast.error(getErrorMessage(error, 'Erro ao remover o paciente'));
		},
	});
};
